<script lang="ts">
	import { SkipBack, ChevronLeft, Play, Pause, ChevronRight, SkipForward } from 'lucide-svelte';
	import type { GraphNode } from '$lib/tree/types';
	import { showTooltip, hideTooltip, moveTooltip } from '$lib/tree/render-common';
	import { getNodeTitle } from '$lib/tree/tooltip';

	interface Props {
		maxSteps: number;
		currentStep: number;
		onStepChange?: (step: number) => void;
		isPlaying?: boolean;
		onTogglePlay?: () => void;
		playbackSpeed?: number;
		onSpeedChange?: (speed: number) => void;
		sidebarCollapsed?: boolean;
		milestoneNodeIds?: string[];
		nodes?: Record<string, GraphNode>;
		icons?: Record<string, string>;
		tooltipElement?: HTMLElement | null;
		onHighlightNode?: (nodeId: string) => void;
		onClearHighlight?: () => void;
		highlightedNodeId?: string | null;
	}

	let {
		maxSteps,
		currentStep,
		onStepChange,
		isPlaying = false,
		onTogglePlay,
		playbackSpeed = 1,
		onSpeedChange,
		sidebarCollapsed = false,
		milestoneNodeIds = [],
		nodes = {},
		icons = {},
		tooltipElement = null,
		onHighlightNode,
		onClearHighlight,
		highlightedNodeId = null
	}: Props = $props();

	let sliderValue = $state(currentStep);
	let isDragging = $state(false);
	let sliderRef: HTMLInputElement;

	// Sync external currentStep with internal sliderValue
	$effect(() => {
		if (!isDragging && currentStep !== sliderValue) {
			sliderValue = currentStep;
		}
	});

	function handleInput(e: Event) {
		const target = e.target as HTMLInputElement;
		sliderValue = parseInt(target.value, 10);
		onStepChange?.(sliderValue);
	}

	function handleMouseDown() {
		isDragging = true;
	}

	function handleMouseUp() {
		isDragging = false;
	}

	function handleKeyDown(e: KeyboardEvent) {
		if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
			e.preventDefault();
			const newStep = Math.max(0, sliderValue - 1);
			sliderValue = newStep;
			onStepChange?.(newStep);
		} else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
			e.preventDefault();
			const newStep = Math.min(maxSteps, sliderValue + 1);
			sliderValue = newStep;
			onStepChange?.(newStep);
		}
	}

	let showSpeedMenu = $state(false);

	$effect(() => {
		if (!showSpeedMenu) return;
		function close(e: MouseEvent) {
			if (!(e.target as HTMLElement).closest('.speed-dropdown')) {
				showSpeedMenu = false;
			}
		}
		document.addEventListener('click', close);
		return () => document.removeEventListener('click', close);
	});

	function stepBackward() {
		const newStep = Math.max(0, sliderValue - 1);
		sliderValue = newStep;
		onStepChange?.(newStep);
	}

	function stepForward() {
		const newStep = Math.min(maxSteps, sliderValue + 1);
		sliderValue = newStep;
		onStepChange?.(newStep);
	}

	function goToStart() {
		sliderValue = 0;
		onStepChange?.(0);
	}

	function goToEnd() {
		sliderValue = maxSteps;
		onStepChange?.(maxSteps);
	}

	// Format step number with leading zero
	function formatStep(step: number): string {
		return step.toString().padStart(2, '0');
	}

	const milestoneEntries = $derived(
		milestoneNodeIds.map((nodeId, index) => {
			const step = index + 1;
			// Map step to percentage, accounting for 18px thumb width
			// The slider track usable width is (containerWidth - 18px)
			// Thumb center moves from 9px to (containerWidth - 9px)
			// For proper alignment, use calc(9px + ratio * (100% - 18px))
			const ratio = maxSteps > 0 ? step / maxSteps : 0;
			return {
				nodeId,
				node: nodes[nodeId],
				leftPos: maxSteps > 0 
					? `calc(9px + ${ratio * 100}% - ${ratio * 18}px)` 
					: '9px',
				active: sliderValue >= step,
				iconHref: nodes[nodeId]?.iconActive || icons[nodeId]
			};
		}).filter((entry) => entry.node)
	);

	function handleMilestoneEnter(event: MouseEvent, nodeId: string, milestoneEl: HTMLElement) {
		const node = nodes[nodeId];
		if (!node || !tooltipElement) return;
		showMilestoneTooltip(node, node.iconActive || icons[nodeId], tooltipElement, milestoneEl);
		onHighlightNode?.(nodeId);
	}

	function handleMilestoneLeave() {
		if (!tooltipElement) return;
		hideTooltip(tooltipElement);
		onClearHighlight?.();
	}

	function showMilestoneTooltip(
		node: GraphNode,
		iconHref: string | undefined,
		tooltip: HTMLElement,
		milestoneEl: HTMLElement
	) {
		import('$lib/tree/tooltip').then(({ generateTooltipHTML }) => {
			tooltip.innerHTML = generateTooltipHTML(node, iconHref);
			tooltip.style.display = 'block';
			
			// Position centered above the milestone
			const rect = milestoneEl.getBoundingClientRect();
			const tooltipRect = tooltip.getBoundingClientRect();
			
			const centerX = rect.left + rect.width / 2;
			const topY = rect.top - tooltipRect.height - 8;
			
			// Keep within viewport bounds
			const finalX = Math.max(10, Math.min(centerX - tooltipRect.width / 2, window.innerWidth - tooltipRect.width - 10));
			const finalY = Math.max(10, topY);
			
			tooltip.style.left = finalX + 'px';
			tooltip.style.top = finalY + 'px';
		});
	}
</script>

<div class="timeline-container" class:collapsed={sidebarCollapsed} role="region" aria-label="Build progression timeline">
	<div class="timeline-controls">
		<button 
			class="control-btn" 
			onclick={goToStart}
			title="Go to start"
			disabled={sliderValue === 0}
		>
			<SkipBack size={16} />
		</button>
		
		<button 
			class="control-btn" 
			onclick={stepBackward}
			title="Step back"
			disabled={sliderValue === 0}
		>
			<ChevronLeft size={16} />
		</button>
		
		<button 
			class="control-btn play-btn"
			class:playing={isPlaying}
			onclick={() => onTogglePlay?.()}
			title={isPlaying ? "Pause" : "Play"}
		>
			{#if isPlaying}
				<Pause size={16} />
			{:else}
				<Play size={16} />
			{/if}
		</button>
		
		<button 
			class="control-btn" 
			onclick={stepForward}
			title="Step forward"
			disabled={sliderValue === maxSteps}
		>
			<ChevronRight size={16} />
		</button>
		
		<button 
			class="control-btn" 
			onclick={goToEnd}
			title="Go to end"
			disabled={sliderValue === maxSteps}
		>
			<SkipForward size={16} />
		</button>

		<div class="speed-dropdown">
			<button 
				class="control-btn speed-toggle"
				onclick={() => showSpeedMenu = !showSpeedMenu}
				title="Playback speed"
			>
				{playbackSpeed}x
			</button>
			{#if showSpeedMenu}
				<div class="speed-menu">
					{#each [0.5, 1, 1.5, 2, 3] as speed (speed)}
						<button
							class="speed-option"
							class:active={playbackSpeed === speed}
							onclick={() => { onSpeedChange?.(speed); showSpeedMenu = false; }}
						>
							{speed}x
						</button>
					{/each}
				</div>
			{/if}
		</div>

		<!-- Step counter - mobile only (hidden on desktop) -->
		<div class="step-display mobile-only">
			<span class="current-step">{formatStep(sliderValue)}</span>
			<span class="separator">/</span>
			<span class="total-steps">{formatStep(maxSteps)}</span>
		</div>
	</div>
	
	<div class="slider-wrapper">
		<div class="slider-track-container">
			<div class="milestone-markers" aria-hidden="true">
				{#each milestoneEntries as entry (entry.nodeId)}
					<button
						type="button"
						class="milestone"
						class:active={entry.active}
						class:highlighted={highlightedNodeId === entry.nodeId}
						style="--milestone-left: {entry.leftPos}"
						onmouseenter={(e) => handleMilestoneEnter(e, entry.nodeId, e.currentTarget as HTMLElement)}
						onmouseleave={handleMilestoneLeave}
						aria-label={entry.node ? `Timeline milestone: ${getNodeTitle(entry.nodeId)}` : entry.nodeId}
					></button>
				{/each}
			</div>
			<input
				bind:this={sliderRef}
				type="range"
				min="0"
				max={maxSteps}
				value={sliderValue}
				class="timeline-slider"
				oninput={handleInput}
				onmousedown={handleMouseDown}
				onmouseup={handleMouseUp}
				onmouseleave={handleMouseUp}
				onkeydown={handleKeyDown}
				aria-label="Build progression slider"
				aria-valuemin={0}
				aria-valuemax={maxSteps}
				aria-valuenow={sliderValue}
			/>
			<div class="track-fill" style="width: calc({(sliderValue / maxSteps) * 100}% - {(sliderValue / maxSteps) * 18}px + 9px)"></div>
		</div>
	</div>

	<!-- Step counter - desktop only (hidden on mobile) -->
	<div class="step-display desktop-only">
		<span class="current-step">{formatStep(sliderValue)}</span>
		<span class="separator">/</span>
		<span class="total-steps">{formatStep(maxSteps)}</span>
	</div>
</div>

<style>
	.timeline-container {
		position: fixed;
		bottom: 0;
		left: 0;
		right: var(--sidebar-width, 320px);
		display: flex;
		align-items: center;
		gap: 16px;
		padding: 12px 20px;
		background: var(--color-bg-overlay);
		backdrop-filter: blur(12px);
		border-top: 1px solid var(--color-border-subtle);
		transition: right 0.2s cubic-bezier(0.4, 0, 0.2, 1);
		z-index: 1002;
	}

	.timeline-container.collapsed {
		right: 0;
	}

	.timeline-controls {
		display: flex;
		align-items: center;
		gap: 6px;
		flex-shrink: 0;
	}

	.control-btn {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 36px;
		height: 36px;
		background: var(--color-bg-glass);
		border: 1px solid var(--color-border-muted);
		border-radius: 8px;
		color: var(--color-text-secondary);
		cursor: pointer;
		transition: all 0.15s ease;
	}

	.control-btn:hover:not(:disabled) {
		background: var(--color-bg-panel-hover);
		border-color: var(--color-border-hover);
		color: var(--color-text-primary);
	}

	.control-btn:disabled {
		opacity: 0.3;
		cursor: not-allowed;
	}

	.control-btn svg {
		width: 20px;
		height: 20px;
	}

	.play-btn {
		background: var(--color-success-soft);
		border-color: var(--color-success);
		color: var(--color-success);
	}

	.play-btn:hover:not(:disabled) {
		background: color-mix(in srgb, var(--color-success) 24%, transparent);
		border-color: var(--color-success);
	}

	.play-btn.playing {
		background: var(--color-danger-soft);
		border-color: var(--color-danger);
		color: var(--color-danger);
	}

	.play-btn.playing:hover {
		background: color-mix(in srgb, var(--color-danger) 24%, transparent);
		border-color: var(--color-danger);
	}

	.slider-wrapper {
		flex: 1;
		display: flex;
		flex-direction: column;
		gap: 4px;
		min-width: 200px;
	}

	.slider-track-container {
		position: relative;
		height: 24px;
		display: flex;
		align-items: center;
	}

	.timeline-slider {
		position: absolute;
		width: 100%;
		height: 100%;
		margin: 0;
		padding: 0;
		background: transparent;
		-webkit-appearance: none;
		appearance: none;
		cursor: pointer;
		z-index: 2;
	}

	.timeline-slider::-webkit-slider-runnable-track {
		width: 100%;
		height: 6px;
		background: var(--color-border-default);
		border-radius: 3px;
	}

	.timeline-slider::-moz-range-track {
		width: 100%;
		height: 6px;
		background: var(--color-border-default);
		border-radius: 3px;
	}

	.timeline-slider::-webkit-slider-thumb {
		-webkit-appearance: none;
		appearance: none;
		width: 18px;
		height: 18px;
		background: var(--color-success);
		border-radius: 50%;
		cursor: grab;
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
		transition: transform 0.1s ease, box-shadow 0.15s ease;
		margin-top: -6px;
	}

	.timeline-slider::-webkit-slider-thumb:hover {
		transform: scale(1.15);
		box-shadow: 0 3px 12px color-mix(in srgb, var(--color-success) 40%, transparent);
	}

	.timeline-slider::-webkit-slider-thumb:active {
		cursor: grabbing;
		transform: scale(1.1);
	}

	.timeline-slider::-moz-range-thumb {
		width: 18px;
		height: 18px;
		background: var(--color-success);
		border-radius: 50%;
		cursor: grab;
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
		border: none;
		transition: transform 0.1s ease, box-shadow 0.15s ease;
	}

	.timeline-slider::-moz-range-thumb:hover {
		transform: scale(1.15);
		box-shadow: 0 3px 12px color-mix(in srgb, var(--color-success) 40%, transparent);
	}

	.timeline-slider::-moz-range-thumb:active {
		cursor: grabbing;
		transform: scale(1.1);
	}

	.timeline-slider:focus {
		outline: none;
	}

	.timeline-slider:focus::-webkit-slider-thumb {
		box-shadow: 0 0 0 3px color-mix(in srgb, var(--color-success) 30%, transparent);
	}

	.track-fill {
		position: absolute;
		top: 50%;
		left: 0;
		transform: translateY(-50%);
		height: 6px;
		background: linear-gradient(90deg, var(--color-success) 0%, color-mix(in srgb, var(--color-success) 82%, black) 100%);
		border-radius: 3px;
		pointer-events: none;
		z-index: 1;
		max-width: calc(100% - 9px);
	}

	.milestone-markers {
		position: absolute;
		width: 100%;
		height: 100%;
		pointer-events: none;
		z-index: 3;
	}

	.milestone {
		position: absolute;
		top: 50%;
		left: var(--milestone-left, 9px);
		transform: translate(-50%, -50%);
		width: 10px;
		height: 10px;
		background: var(--color-border-strong);
		border-radius: 50%;
		border: 1px solid var(--color-bg-surface);
		transition: background 0.2s ease, transform 0.15s ease, box-shadow 0.15s ease;
		pointer-events: auto;
		cursor: pointer;
		padding: 0;
		margin: 0;
	}

	.milestone.active {
		background: var(--color-success);
		box-shadow: 0 0 6px color-mix(in srgb, var(--color-success) 50%, transparent);
	}

	.milestone.highlighted {
		background: var(--color-accent-primary);
		box-shadow: 0 0 8px color-mix(in srgb, var(--color-accent-primary) 60%, transparent);
		border-color: color-mix(in srgb, var(--color-accent-primary) 80%, transparent);
	}

	.milestone:hover {
		transform: translate(-50%, -50%) scale(1.2);
		box-shadow: 0 0 0 4px var(--color-bg-subtle-strong);
	}

	.milestone.highlighted:hover {
		box-shadow: 0 0 0 4px color-mix(in srgb, var(--color-accent-primary) 30%, transparent), 0 0 8px color-mix(in srgb, var(--color-accent-primary) 60%, transparent);
	}

	.step-display {
		display: flex;
		align-items: center;
		gap: 4px;
		padding: 8px 12px;
		background: var(--color-bg-glass);
		border: 1px solid var(--color-border-muted);
		border-radius: 8px;
		font-family: 'SF Mono', Monaco, monospace;
		font-size: 14px;
		font-weight: 600;
		color: var(--color-text-primary);
		flex-shrink: 0;
		min-width: 70px;
		justify-content: center;
	}

	.current-step {
		color: var(--color-success);
	}

	.separator {
		color: var(--color-text-muted);
		opacity: 0.5;
	}

	.total-steps {
		color: var(--color-text-tertiary);
	}

	.speed-dropdown {
		position: relative;
	}

	.speed-toggle {
		font-size: 12px;
		font-weight: 600;
		min-width: 36px;
	}

	.speed-menu {
		position: absolute;
		bottom: calc(100% + 6px);
		left: 50%;
		transform: translateX(-50%);
		display: flex;
		flex-direction: column;
		background: var(--color-bg-overlay);
		backdrop-filter: blur(12px);
		border: 1px solid var(--color-border-default);
		border-radius: 8px;
		padding: 4px;
		z-index: 1010;
		min-width: 56px;
	}

	.speed-option {
		padding: 6px 12px;
		background: transparent;
		border: none;
		border-radius: 6px;
		color: var(--color-text-tertiary);
		font-size: 12px;
		font-weight: 500;
		cursor: pointer;
		text-align: center;
		transition: all 0.1s ease;
	}

	.speed-option:hover {
		background: var(--color-bg-subtle-strong);
		color: var(--color-text-primary);
	}

	.speed-option.active {
		color: var(--color-success);
		background: var(--color-success-soft);
	}

	/* Show/hide step displays based on viewport - desktop unchanged, mobile adjusted */
	.step-display.mobile-only {
		display: none;
	}

	@media (max-width: 768px) {
		.step-display.desktop-only {
			display: none;
		}

		.step-display.mobile-only {
			display: flex;
		}
		.timeline-container {
			padding: 8px 12px 60px; /* Extra bottom padding for drawer handle */
			gap: 8px;
			/* Stack vertically: slider on top, buttons below */
			flex-direction: column-reverse;
			align-items: stretch;
			height: auto;
		}

		/* Center all controls together */
		.timeline-controls {
			justify-content: center;
			gap: 8px;
			flex-wrap: nowrap;
		}

		.control-btn {
			width: 40px;
			height: 40px;
		}

		.control-btn svg {
			width: 20px;
			height: 20px;
		}

		/* Speed toggle next to step display */
		.speed-dropdown {
			order: 0;
			margin-left: 0;
		}

		.speed-toggle {
			width: auto;
			padding: 0 12px;
			font-size: 12px;
			height: 40px;
		}

		/* Step display next to speed */
		.step-display {
			padding: 8px 12px;
			font-size: 12px;
			min-width: auto;
			position: static;
			order: 0;
			background: var(--color-bg-glass);
			border: 1px solid var(--color-border-muted);
			border-radius: 6px;
		}

		/* Larger touch targets for mobile */
		.timeline-slider {
			height: 32px;
		}

		.slider-wrapper {
			padding: 4px 0;
		}

		/* Hide milestone markers on mobile - too crowded */
		.milestone-markers {
			display: none;
		}
	}
</style>
